import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { readPurchase } from 'apis/restaurant-api/merchant-admin/purchases/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import CompensationOfferBanner from 'modules/insights-v2/components/ratings/CompensationOffers/CompensationOfferBanner';

import PurchaseDetails from './PurchaseDetails';

type PurchaseDetailLoaderViewProps = {
  orderComment?: string;
  selectedPurchaseId: string;
  showDeliveryFees: boolean;
  shouldDeliveryFeesIncludeServiceFees: boolean;
  venue: Venue;
  onClose: () => void;
  sentOffer?: CreditAcquisition;
  onSendOffer?: () => void;
};

const PurchaseDetailLoaderViewContainer = styled(WoltModal).attrs({ position: 'right' })`
  background-color: ${cbSemanticCssVars.surfaceMain};
  width: 400px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding-block-start: 32px;
`;

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 12px;
  height: 100px;
  max-width: 95%;
`;

const PurchaseDetailLoaderView = ({
  orderComment,
  selectedPurchaseId,
  showDeliveryFees,
  shouldDeliveryFeesIncludeServiceFees,
  venue,
  onClose,
  sentOffer,
  onSendOffer,
}: PurchaseDetailLoaderViewProps) => {
  const {
    data: purchase = null,
    isError,
    isLoading,
  } = useQuery([`/v1/merchant-admin/venues/${venue.id}/purchases/${selectedPurchaseId}`], () =>
    readPurchase({ venueId: venue.id, purchaseId: selectedPurchaseId }),
  );

  return (
    <PurchaseDetailLoaderViewContainer onRequestClose={onClose} isOpen>
      {isLoading ? (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      ) : isError ? (
        <ErrorAlert>
          <FormattedMessage id="errors.unknown-error-retry-contact" />
        </ErrorAlert>
      ) : (
        purchase !== null && (
          <PurchaseDetails
            onClose={onClose}
            purchase={purchase}
            timezone={venue.timezone}
            menuItemCurrency={venue.currency}
            showDeliveryFees={showDeliveryFees}
            deliveryFeesIncludeServiceFees={shouldDeliveryFeesIncludeServiceFees}
            showTaxID={venue.country === 'POL'}
            orderComment={orderComment}
            compensationOffer={sentOffer}
            compensationOfferBanner={
              !!onSendOffer && (
                <CompensationOfferBanner
                  venueId={venue.id}
                  currency={venue.currency}
                  purchaseId={purchase.id}
                  rating={purchase.review?.score ?? undefined}
                  onSendOffer={onSendOffer}
                />
              )
            }
          />
        )
      )}
    </PurchaseDetailLoaderViewContainer>
  );
};

export default PurchaseDetailLoaderView;
